import { FieldKey } from '@marketiq/calc-core';

/**
 * Resolves a symbolic {@link FieldKey} to the key the host's reactive `FormGroup` uses for that
 * field. In MarketIQ that control key is the snake_case DB field name (e.g. `'base_rent_yearly'`),
 * though it may be a numeric catalog column id in some sections — hence `string | number`.
 *
 * The concrete map lives in the HOST app (`BuiltInColumnIdMap`, Phase 5), not in this library or in
 * calc-core, so that different catalog deployments can ship the same calc build. A startup-time
 * assertion in the host verifies every rule's input/output FieldKey resolves here.
 */
export interface ColumnIdMap {
  /** Control key (FormGroup control name) for a FieldKey, or null/undefined when unmapped. */
  idFor(key: FieldKey): string | number | null | undefined;
  /** Optional reverse lookup: FieldKey for a given control key. */
  keyFor?(controlKey: string | number): FieldKey | undefined;
  /**
   * Numeric catalog Column id for a FieldKey — used by the rule-engine / `ColumnSectionCalculationContext`
   * path, where data is keyed by `columnId` rather than control name.
   */
  columnIdFor?(key: FieldKey): number | null | undefined;
  /** Reverse of {@link columnIdFor}: FieldKey for a numeric Column id. */
  keyForColumnId?(columnId: number): FieldKey | undefined;
}

/**
 * Identity map: treats the FieldKey's own string value as the control key. Useful only for tests
 * and the MemoryCalculationContext path; real hosts provide a populated {@link ColumnIdMap}.
 */
export class IdentityColumnIdMap implements ColumnIdMap {
  idFor(key: FieldKey): string {
    return key;
  }
  keyFor(controlKey: string | number): FieldKey | undefined {
    return Object.values(FieldKey).includes(controlKey as FieldKey) ? (controlKey as FieldKey) : undefined;
  }
}
